import {
  ApiItemType,
  ApiReturnMeasurePoint,
} from '@highimpact-frontend/api-types';
import TabComponent, {
  MUITabItemSettings as TabSettings,
} from '../../../components/utils/MUISpecifics/tabs/TabComponent';
import StateDecorator from '../../../components/utils/state/StateDecorator';
import { isDataState } from '../../../store/data/state';
import { useAppSelector } from '../../../store/hooks';
import assert from 'assert';
import { forwardRef, LegacyRef } from 'react';
import { useParams } from 'react-router-dom';
import SiteDecorator from '../utils/SiteDecorator';
import AlertConfigList from './AlertConfigList';
import AlertList from './AlertList';
import { usePermission } from '../../../components/utils/permission/helpers/usePermission';
import { useTranslation } from 'react-i18next';

const isActiveMeasurePoint =
  (site_id: string | undefined) => (mp: ApiReturnMeasurePoint) => {
    const hasDeviceSite = mp.device_site !== null;
    const noEndAssignmentDate =
      (mp.device_site?.end_assignement_date ?? null) === null;
    const endDate = new Date(mp.device_site?.end_assignement_date!).getTime();
    const nowDate = Date.now();
    const isActive =
      (hasDeviceSite && noEndAssignmentDate) || endDate > nowDate;
    const isForSite =
      site_id !== undefined &&
      mp.device_site?.site_id === Number.parseInt(site_id);
    return isActive && isForSite;
  };

function AlertView(_props: object, ref: LegacyRef<HTMLDivElement>) {
  const { site_id, project_id } = useParams<{
    project_id: string;
    site_id: string;
  }>();

  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const stations = useAppSelector(
    (state) => state.data_slice.externals.measure_points
  );
  const devices = useAppSelector((state) => state.data_slice.externals.devices);
  const devicesTypes = useAppSelector(
    (state) => state.data_slice.externals.device_types
  );
  const device_type_measure_types = useAppSelector(
    (state) => state.data_slice.externals.device_type_measure_types
  );
  const measure_types = useAppSelector(
    (state) => state.data_slice.externals.measure_types
  );
  const measure_points = useAppSelector(
    (state) => state.data_slice.externals.measure_points
  );

  //Obtain permissions for each tab
  const alertsPermission = usePermission('alerts_tab', project_id, site_id);

  const alertsConfigPermission = usePermission(
    'alerts_configurations_tab',
    project_id,
    site_id
  );

  const { t } = useTranslation('alerts');

  return SiteDecorator(ref, () => {
    return StateDecorator(
      [
        sites,
        stations,
        devices,
        devicesTypes,
        device_type_measure_types,
        measure_types,
        measure_points,
      ],
      ref,
      () => {
        assert(isDataState(sites));
        assert(isDataState(stations));
        assert(isDataState(devices));
        assert(isDataState(devicesTypes));
        assert(isDataState(device_type_measure_types));
        assert(isDataState(measure_types));
        assert(isDataState(measure_points));

        const activeStations = Object.values(stations.data).filter(
          isActiveMeasurePoint(site_id)
        );

        if (!site_id || !project_id)
          return (
            <div>
              Les données du projet et du site n'ont pas pu être récupérées
            </div>
          );

        const site = Object.values(sites.data).find(
          (key) => key.id.toString() === site_id
        );

        if (!site)
          return <div>Les données site n'ont pas pu être récupérées</div>;
        const alerts = (
          <AlertList
            projectId={Number.parseInt(project_id)}
            siteId={Number.parseInt(site_id)}
            devices={Object.values(devices.data)}
            deviceTypes={Object.values(devicesTypes.data)}
            measurePoints={Object.values(measure_points.data)}
            measureTypes={Object.values(measure_types.data)}
          />
        );

        const monitoringList = (
          <AlertConfigList
            stations={activeStations}
            project_id={Number.parseInt(project_id)}
            site_id={Number.parseInt(site_id)}
            devices={Object.values(devices.data)}
            deviceTypeMeasureTypes={Object.values(
              device_type_measure_types.data
            )}
            measureTypes={Object.values(measure_types.data)}
          />
        );

        /** Tabs with permissions */
        const tabsPerm: TabSettings[] = [];
        if (alertsPermission.shouldRender) {
          tabsPerm.push({
            label: t('Alertes'),
            itemName: 'alerts_tab' as ApiItemType,
            children: alertsPermission.message ? (
              <div>{alertsPermission.message}</div>
            ) : (
              alerts
            ),
            action: alertsPermission.action,
          });
        }

        if (alertsConfigPermission.shouldRender) {
          tabsPerm.push({
            label: t('Configurations'),
            itemName: 'alerts_configurations_tab' as ApiItemType,
            children: alertsConfigPermission.message ? (
              <div>{alertsConfigPermission.message}</div>
            ) : (
              monitoringList
            ),
            action: alertsConfigPermission.action,
          });
        }

        return (
          <div className="list">
            <TabComponent tabs={tabsPerm} storageKey="alerts-tabs" />
          </div>
        );
      }
    );
  });
}

export default forwardRef(AlertView);
